import type { PostgrestResponse, PostgrestSingleResponse } from "@supabase/postgrest-js";
import type { Database } from "../../../__definitions__/database-definitions";
import {
  SupabaseService,
  SupabaseServiceView,
} from "../../../server/supabase/base-supabase-service";
import { toISO8601 } from "../../../utils/iso8601";
import type { UUID } from "../../../utils/uuid";
import type { PollSchema } from "../../message/editor/plugins/poll-plugin/types";

export type PollWithOptions = Database["public"]["Views"]["poll_with_options"]["Row"];
export type PollWithOptionsResults =
  Database["public"]["Views"]["poll_with_options_results"]["Row"];
export type PresetPollOptionsType =
  Database["public"]["Views"]["preset_poll_options_with_icons"]["Row"];
export type PresetPollQuestionWithOptions =
  Database["public"]["Views"]["preset_poll_question_with_options"]["Row"];

type UpsertPollWithOptionIdsInput = {
  accountId: UUID;
  pollId: UUID;
  question: string;
  options: { id: UUID; text: string; ordering: number }[];
};

export class SupabasePollService extends SupabaseService<"poll"> {
  table = "poll" as const;

  async upsertPoll(
    poll: Omit<PollSchema, "answer_type">,
  ): Promise<PostgrestSingleResponse<boolean>> {
    return this.log_errors(this.client.rpc("upsert_poll", { poll_: poll }));
  }

  async upsertPollWithOptionIds({
    accountId,
    pollId,
    question,
    options,
  }: UpsertPollWithOptionIdsInput): Promise<void> {
    const pollResponse = this.log_errors(
      await this.client
        .from("poll")
        .upsert({
          id: pollId,
          account_id: accountId,
          question,
          helper_text: null,
          show_poll_results: false,
        })
        .select("id")
        .single(),
    );
    if (pollResponse.error) throw new Error(pollResponse.error.message);

    const optionIds = options.map((option) => option.id);
    const existingOptionsResponse =
      optionIds.length > 0
        ? this.log_errors(await this.client.from("poll_option").select("id").in("id", optionIds))
        : { data: [], error: null };
    if (existingOptionsResponse.error) throw new Error(existingOptionsResponse.error.message);

    const existingOptionIds = new Set((existingOptionsResponse.data ?? []).map(({ id }) => id));
    const newOptions = options.filter((option) => !existingOptionIds.has(option.id));
    const existingOptions = options.filter((option) => existingOptionIds.has(option.id));

    const currentOptionsResponse = this.log_errors(
      await this.client
        .from("poll_option")
        .select("id")
        .eq("poll_id", pollId)
        .is("deleted_at", null),
    );
    if (currentOptionsResponse.error) throw new Error(currentOptionsResponse.error.message);

    const activeOptionIds = new Set(options.map((option) => option.id));
    const removedOptionIds = (currentOptionsResponse.data ?? [])
      .map((option) => option.id)
      .filter((id) => !activeOptionIds.has(id));

    if (removedOptionIds.length > 0) {
      const removedOptionsResponse = this.log_errors(
        await this.client
          .from("poll_option")
          .update({ deleted_at: toISO8601(new Date()), ordering: null })
          .in("id", removedOptionIds),
      );
      if (removedOptionsResponse.error) throw new Error(removedOptionsResponse.error.message);
    }

    for (const option of existingOptions) {
      const optionUpdateResponse = this.log_errors(
        await this.client
          .from("poll_option")
          .update({
            option_text: option.text,
            ordering: option.ordering,
            deleted_at: null,
          })
          .eq("id", option.id),
      );
      if (optionUpdateResponse.error) throw new Error(optionUpdateResponse.error.message);
    }

    if (newOptions.length === 0) return;

    const optionInsertResponse = this.log_errors(
      await this.client.from("poll_option").insert(
        newOptions.map((option) => ({
          id: option.id,
          account_id: accountId,
          poll_id: pollId,
          option_text: option.text,
          ordering: option.ordering,
          deleted_at: null,
        })),
      ),
    );
    if (optionInsertResponse.error) throw new Error(optionInsertResponse.error.message);
  }

  async getPollsWithOptions(poll_ids: UUID[]): Promise<PostgrestResponse<PollWithOptions>> {
    return this.log_errors(this.client.from("poll_with_options").select("*").in("id", poll_ids));
  }

  async getPollsWithOptionsResults(
    poll_ids: UUID[],
  ): Promise<PostgrestSingleResponse<PollWithOptionsResults[]>> {
    return this.log_errors(
      this.client.from("poll_with_options_results").select("*").in("id", poll_ids),
    );
  }
}

export class SupabasePresetPollAnswerService extends SupabaseServiceView<"preset_poll_options_with_icons"> {
  table = "preset_poll_options_with_icons" as const;
}

export class SupabasePresetPollQuestionService extends SupabaseServiceView<"preset_poll_question_with_options"> {
  table = "preset_poll_question_with_options" as const;
}
